import { useEffect, useState } from "react";
import { CONTACT } from "@/config/contact";
import { track, trackCTAClick } from "@/lib/analytics";
import { Calendar, Mail, Phone, X, Share2, Facebook, Linkedin, Instagram, Youtube } from "lucide-react";
import Button from "@/components/ui/Button";
import { useUI } from "@/contexts/UIContext";
import { useLocalizedPath } from "@/lib/locale";
import { useLanguage } from "@/contexts/LanguageContext";

type Panel = "none" | "consult" | "contact" | "social";

export default function FloatingQuickActions() {
  const { isAIAssistantOpen } = useUI();
  const [open, setOpen] = useState<Panel>("none");
  const localizedPath = useLocalizedPath();
  const { language } = useLanguage();
  const isGreek = language === "el";

  const copy = {
    consultTitle: isGreek ? "Δωρεάν Συμβουλευτική" : "Free consultation",
    consultDescription: isGreek
      ? "Κλείστε μια δωρεάν συμβουλευτική κλήση και λάβετε αναλυτική πρόταση μέσα σε 24 ώρες."
      : "Book a free consultation call and receive a tailored proposal within 24 hours.",
    consultCta: isGreek ? "Δωρεάν Συμβουλευτική →" : "Book a free consultation →",
    close: isGreek ? "Κλείσιμο" : "Close",
    contactAria: isGreek ? "Επικοινωνία" : "Contact",
    contactTitle: isGreek ? "Επικοινωνία" : "Contact",
    callNow: isGreek ? "Κάλεσε Τώρα" : "Call now",
    socialTitle: isGreek ? "Social Media" : "Social media",
  };

  // esc to close
  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => e.key === "Escape" && setOpen("none");
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, []);

  // helper to toggle
  const toggle = (p: Panel) => setOpen(cur => (cur === p ? "none" : p));

  if (isAIAssistantOpen) {
    return null;
  }

  return (
    <div
      className="
        fixed right-2 lg:right-6
        bottom-24 lg:bottom-auto lg:top-1/2 lg:-translate-y-1/2
        z-50 hidden lg:flex lg:flex-col gap-3
      "
      aria-live="polite"
    >
      {/* CONSULT FAB */}
      <div className="relative">
        <button
          aria-label={copy.consultTitle}
          aria-expanded={open === "consult"}
          aria-controls="qa-consult"
          onClick={() => { toggle("consult"); track("cta_click", { where: "fab_consult_toggle" }); }}
          className="
            h-12 w-12 rounded-full bg-white/10 border border-white/15 transition-all duration-300 hover:bg-white/20 hover:border-white/25 text-white
            grid place-items-center shadow-lg shadow-black/40 transition-all
            hover:scale-105
            focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/40
          "
        >
          <Calendar size={20} aria-hidden="true" />
        </button>

        {/* Consult panel */}
        {open === "consult" && (
          <div
            id="qa-consult"
            role="dialog"
            aria-label={copy.consultTitle}
            className="
              absolute right-14 md:right-14 top-1/2 -translate-y-1/2
              w-[300px] rounded-2xl p-4
              bg-black/70 backdrop-blur-xl border border-white/15 text-white
              shadow-2xl shadow-black/40
            "
          >
            <div className="flex items-start justify-between">
              <h3 className="text-sm font-semibold">{copy.consultTitle}</h3>
              <button
                aria-label={copy.close}
                onClick={() => setOpen("none")}
                className="rounded-md p-1 text-white/70 hover:text-white hover:bg-white/10"
              >
                <X size={16} aria-hidden="true" />
              </button>
            </div>
            <p className="mt-2 text-sm text-white/80">
              {copy.consultDescription}
            </p>
            <div className="mt-3 grid gap-2">
              <Button
                href={localizedPath("/consultation")}
                onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                  event.preventDefault();
                  trackCTAClick(copy.consultCta, "floating_quick_actions", "primary");
                  setOpen("none");
                  window.navigateToConsultation?.();
                }}
                size="base"
                className="justify-center"
              >
                {copy.consultCta}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* CONTACT FAB */}
      <div className="relative">
        <button
          aria-label={copy.contactAria}
          aria-expanded={open === "contact"}
          aria-controls="qa-contact"
          onClick={() => { toggle("contact"); track("cta_click", { where: "fab_contact_toggle" }); }}
          className="
            h-12 w-12 rounded-full bg-white/10 border border-white/15 transition-all duration-300 hover:bg-white/20 hover:border-white/25 text-white
            grid place-items-center shadow-lg shadow-black/40 transition-all
            hover:scale-105
            focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/40
          "
        >
          <Phone size={20} aria-hidden="true" />
        </button>

        {/* Contact panel */}
        {open === "contact" && (
          <div
            id="qa-contact"
            role="dialog"
            aria-label={copy.contactAria}
            className="
              absolute right-14 md:right-14 top-1/2 -translate-y-1/2
              w-[300px] rounded-2xl p-4
              bg-black/70 backdrop-blur-xl border border-white/15 text-white
              shadow-2xl shadow-black/40
            "
          >
            <div className="flex items-start justify-between">
              <h3 className="text-sm font-semibold">{copy.contactTitle}</h3>
              <button
                aria-label={copy.close}
                onClick={() => setOpen("none")}
                className="rounded-md p-1 text-white/70 hover:text-white hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/40"
              >
                <X size={16} aria-hidden="true" />
              </button>
            </div>

            <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
              <a
                href={CONTACT.phoneHref}
                aria-label={copy.callNow}
                className="flex items-center justify-center gap-2 rounded-xl px-3 py-2 bg-white/10 border border-white/15 transition-all duration-300 hover:bg-white/20 hover:border-white/25 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/40"
              >
                <Phone size={16} aria-hidden="true" />
              </a>
              <a
                href={`mailto:${CONTACT.email}`}
                aria-label="Email"
                className="flex items-center justify-center gap-2 rounded-xl px-3 py-2 bg-white/10 border border-white/15 transition-all duration-300 hover:bg-white/20 hover:border-white/25 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/40"
              >
                <Mail size={16} aria-hidden="true" />
              </a>
            </div>
          </div>
        )}
      </div>

      {/* SOCIAL FAB */}
      <div className="relative">
        <button
          aria-label={copy.socialTitle}
          aria-expanded={open === "social"}
          aria-controls="qa-social"
          onClick={() => { toggle("social"); track("cta_click", { where: "fab_social_toggle" }); }}
          className="
            h-12 w-12 rounded-full bg-white/10 border border-white/15 transition-all duration-300 hover:bg-white/20 hover:border-white/25 text-white
            grid place-items-center shadow-lg shadow-black/40 transition-all
            hover:scale-105
            focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/40
          "
        >
          <Share2 size={20} aria-hidden="true" />
        </button>

        {/* Social panel */}
        {open === "social" && (
          <div
            id="qa-social"
            role="dialog"
            aria-label={copy.socialTitle}
            className="
              absolute right-14 md:right-14 top-1/2 -translate-y-1/2
              w-[300px] rounded-2xl p-4
              bg-black/70 backdrop-blur-xl border border-white/15 text-white
              shadow-2xl shadow-black/40
            "
          >
            <div className="flex items-start justify-between">
              <h3 className="text-sm font-semibold">{copy.socialTitle}</h3>
              <button
                aria-label={copy.close}
                onClick={() => setOpen("none")}
                className="rounded-md p-1 text-white/70 hover:text-white hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/40"
              >
                <X size={16} aria-hidden="true" />
              </button>
            </div>

            <div className="mt-2 grid grid-cols-4 gap-2 text-sm">
              <a
                href={CONTACT.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                onClick={() => track("outbound_click", { where: "fab_social", platform: "facebook" })}
                className="flex items-center justify-center gap-2 rounded-xl px-3 py-2 bg-white/10 border border-white/15 transition-all duration-300 hover:bg-white/20 hover:border-white/25 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/40"
              >
                <Facebook className="h-5 w-5" aria-hidden="true" />
              </a>
              <a
                href={CONTACT.social.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                onClick={() => track("outbound_click", { where: "fab_social", platform: "linkedin" })}
                className="flex items-center justify-center gap-2 rounded-xl px-3 py-2 bg-white/10 border border-white/15 transition-all duration-300 hover:bg-white/20 hover:border-white/25 transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/40"
              >
                <Linkedin className="h-5 w-5" aria-hidden="true" />
              </a>
              <a
                href={CONTACT.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                onClick={() => track("outbound_click", { where: "fab_social", platform: "instagram" })}
                className="flex items-center justify-center gap-2 rounded-xl px-3 py-2 bg-white/10 border border-white/15 transition-all duration-300 hover:bg-white/20 hover:border-white/25 transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/40"
              >
                <Instagram className="h-5 w-5" aria-hidden="true" />
              </a>
              <a
                href={CONTACT.social.youtube}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                onClick={() => track("outbound_click", { where: "fab_social", platform: "youtube" })}
                className="flex items-center justify-center gap-2 rounded-xl px-3 py-2 bg-white/10 border border-white/15 transition-all duration-300 hover:bg-white/20 hover:border-white/25 transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/40"
              >
                <Youtube className="h-5 w-5" aria-hidden="true" />
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
